import { useEffect, useRef, useCallback, useState } from 'react';

interface UseIdleTimeoutOptions {
  idleTimeoutMs?: number;
  warningTimeMs?: number;
  onIdle: () => void;
  onWarning?: () => void;
  onActive?: () => void;
  enabled?: boolean;
}

const IDLE_TIMEOUT_MS = 15 * 60 * 1000;
const WARNING_TIME_MS = 2 * 60 * 1000;

const ACTIVITY_EVENTS = [
  'mousemove',
  'mousedown',
  'keydown',
  'scroll',
  'touchstart',
  'click',
  'wheel',
];

export function useIdleTimeout({
  idleTimeoutMs = IDLE_TIMEOUT_MS,
  warningTimeMs = WARNING_TIME_MS,
  onIdle,
  onWarning,
  onActive,
  enabled = true,
}: UseIdleTimeoutOptions) {
  const [isWarning, setIsWarning] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(
    Math.floor(warningTimeMs / 1000),
  );

  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null,
  );
  const isWarningRef = useRef(false);

  const clearAllTimers = useCallback(() => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
    }
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
    }
  }, []);

  const startCountdown = useCallback(() => {
    const deadlineAt = Date.now() + warningTimeMs;

    setRemainingSeconds(Math.floor(warningTimeMs / 1000));

    countdownIntervalRef.current = setInterval(() => {
      const remaining = Math.max(
        0,
        Math.ceil((deadlineAt - Date.now()) / 1000),
      );
      setRemainingSeconds(remaining);

      if (remaining <= 0) {
        if (countdownIntervalRef.current) {
          clearInterval(countdownIntervalRef.current);
        }
      }
    }, 500);
  }, [warningTimeMs]);

  const resetTimers = useCallback(() => {
    if (!enabled) {
      return;
    }

    clearAllTimers();

    if (isWarningRef.current) {
      isWarningRef.current = false;
      setIsWarning(false);
      onActive?.();
    }

    warningTimerRef.current = setTimeout(() => {
      isWarningRef.current = true;
      setIsWarning(true);
      startCountdown();
      onWarning?.();
    }, idleTimeoutMs - warningTimeMs);

    idleTimerRef.current = setTimeout(() => {
      clearAllTimers();
      isWarningRef.current = false;
      setIsWarning(false);
      onIdle();
    }, idleTimeoutMs);
  }, [
    enabled,
    idleTimeoutMs,
    warningTimeMs,
    onIdle,
    onWarning,
    onActive,
    clearAllTimers,
    startCountdown,
  ]);

  const dismissWarning = useCallback(() => {
    resetTimers();
  }, [resetTimers]);

  useEffect(() => {
    if (!enabled) {
      clearAllTimers();
      setIsWarning(false);
      return;
    }

    resetTimers();

    const handleActivity = () => {
      if (isWarningRef.current) {
        return;
      }
      resetTimers();
    };

    ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    return () => {
      clearAllTimers();
      ACTIVITY_EVENTS.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [enabled]);

  return { isWarning, remainingSeconds, dismissWarning };
}
